// 2026-10-26 · Centro de control (admin): pestañas en la URL, enlaces a la
// ficha de un restaurante y redirecciones de las URL antiguas (Live debug y
// Versiones). Funciones puras: se prueban sin montar nada.

export const CONTROL_ROOT = "/app/admin/control";

/** Pestañas de la vista global. La primera es la de por defecto. */
export const CONTROL_TABS = ["estado", "versiones", "workers"];

/** Pestañas de la ficha de un restaurante. La primera es la de por defecto. */
export const RESTAURANT_TABS = ["mapa", "timeline", "clics", "comms", "versiones", "tpv", "impresoras", "hacienda"];

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const isGuid = (v) => typeof v === "string" && GUID_RE.test(v);

/** Pestaña pedida en `?tab=`; si no es una de `allowed`, la primera. */
export function parseTab(search, allowed) {
  const q = new URLSearchParams(search || "");
  const tab = q.get("tab");
  return allowed.includes(tab) ? tab : allowed[0];
}

/**
 * Misma query con otra pestaña. Al cambiar de pestaña se quitan los
 * parámetros de un enlace directo (action/at/device): ya se han usado.
 */
export function withTab(search, tab) {
  const q = new URLSearchParams(search || "");
  q.set("tab", tab);
  ["action", "at", "device"].forEach((k) => q.delete(k));
  return `?${q.toString()}`;
}

/** URL de la vista global en una pestaña. */
export function controlUrl(tab = "estado") {
  return `${CONTROL_ROOT}?tab=${encodeURIComponent(tab)}`;
}

/**
 * URL de la ficha de un restaurante. `opts`: tab, name, action, at (ISO o
 * ms), device. Sin rid válido → null.
 */
export function controlRestaurantUrl(rid, { tab = "mapa", name, action, at, device } = {}) {
  if (!isGuid(rid)) return null;
  const q = new URLSearchParams({ tab });
  if (name) q.set("name", name);
  if (action) q.set("action", action);
  if (at !== undefined && at !== null && at !== "") {
    const d = new Date(typeof at === "number" ? at : Date.parse(at));
    if (!Number.isNaN(d.getTime())) q.set("at", d.toISOString());
  }
  if (device) q.set("device", device);
  return `${CONTROL_ROOT}/r/${rid}?${q.toString()}`;
}

/**
 * Enlace directo antiguo de Live debug (?rid&name&action&at).
 * Sin rid válido → null.
 */
export function parseLiveDebugLink(search) {
  if (!search) return null;
  const q = new URLSearchParams(search);
  const rid = q.get("rid");
  if (!isGuid(rid)) return null;
  const atMs = q.get("at") ? Date.parse(q.get("at")) : NaN;
  return {
    rid,
    name: q.get("name") || null,
    action: q.get("action") || null,
    at: Number.isNaN(atMs) ? null : atMs,
  };
}

/**
 * Parámetros del enlace directo dentro de la ficha (?action&at&device&name).
 * `at` en ms o null.
 */
export function parseRestaurantDeepLink(search) {
  const q = new URLSearchParams(search || "");
  const atMs = q.get("at") ? Date.parse(q.get("at")) : NaN;
  return {
    name: q.get("name") || null,
    action: q.get("action") || null,
    at: Number.isNaN(atMs) ? null : atMs,
    device: q.get("device") || null,
  };
}

/**
 * Destino de la URL antigua `/admin/live-debug`:
 *   - con ?rid válido → ficha del restaurante en la línea temporal (con
 *     action/at/name si venían);
 *   - sin rid → vista global, pestaña Estado.
 */
export function legacyLiveDebugTarget(search) {
  const link = parseLiveDebugLink(search);
  if (!link) return controlUrl("estado");
  return controlRestaurantUrl(link.rid, {
    tab: "timeline",
    name: link.name,
    action: link.action,
    at: link.at,
  });
}

/** Destino de la URL antigua `/admin/versions` (con ?tab=workers → Workers). */
export function legacyVersionsTarget(search) {
  const q = new URLSearchParams(search || "");
  return controlUrl(q.get("tab") === "workers" ? "workers" : "versiones");
}

/** Destinos de los enlaces del panel de una incidencia (IncidentDrawer). */
export const incidentUrls = {
  map: (rid, name) => controlRestaurantUrl(rid, { tab: "mapa", name }),
  device: (rid, name, deviceId) => controlRestaurantUrl(rid, { tab: "timeline", name, device: deviceId }),
  action: (rid, name, actionId, at) => controlRestaurantUrl(rid, { tab: "timeline", name, action: actionId, at }),
};
